/**
 * "Artistas · arrastra para ordenar" of Figma CMS-04: the ordered credit list of a song.
 *
 * The array order is `song_artists.position` (docs/02 §1), so ordering is a first-class edit:
 * chips can be dragged (HTML5 drag and drop) **and** moved with two buttons, which keeps the
 * control usable with a keyboard and deterministic in tests. Searching calls
 * `GET /cms/artists?q=` (server-side, docs/06 §4); "crear nuevo" posts `POST /cms/artists` and
 * appends the new artist without leaving the editor.
 */
import type { ArtistRef, CmsArtistsResponse } from '@akpl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent as ReactKeyboardEvent, useId, useRef, useState } from 'react';

import { api } from '../../api';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { Button } from '../ui/button';
import { Input, Label } from '../ui/field';

export interface ArtistSelectProps {
  value: ArtistRef[];
  onChange: (next: ArtistRef[]) => void;
  disabled?: boolean;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/**
 * QA 2026-09-15 **UX-574**: this was the one entity picker PR #28 never reached. A bare
 * `<input>` over a `<div>` of buttons — `role`, `aria-expanded`, `aria-controls`,
 * `aria-autocomplete` and `aria-activedescendant` all null, no `role=listbox`, not one
 * `role=option`, ArrowDown dead (so the list was mouse-only) and picking a result threw focus to
 * `<body>`, which is exactly the UX-538 defect fixed for the Home entity picker. The console had
 * two entity pickers with two different contracts; this one now follows `EntitySearch`'s.
 */
export function ArtistSelect({ value, onChange, disabled = false }: ArtistSelectProps) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const term = search.trim();
  const results = useQuery<CmsArtistsResponse>({
    queryKey: ['cms', 'artists', 'picker', term],
    queryFn: () => api.cmsArtists({ query: { q: term, pageSize: 8 } }),
    enabled: open && term.length > 0,
  });

  const create = useMutation({
    mutationFn: (name: string) => api.cmsArtistCreate({ body: { name } }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'artists'] });
      add({ id: created.artist.id, name: created.artist.name });
    },
  });

  /** UX-574: the chip list replaces the focused button, so focus goes back to the field. */
  const add = (artist: ArtistRef) => {
    setSearch('');
    setOpen(false);
    setActive(0);
    input.current?.focus();
    if (value.some((a) => a.id === artist.id)) return;
    onChange([...value, artist]);
  };

  const candidates = (results.data?.items ?? []).filter(
    (artist) => !value.some((a) => a.id === artist.id),
  );

  const listOpen = open && term.length > 0;
  /** The «+ crear» entry is the last option, so ArrowDown reaches it like any other. */
  const createIndex = candidates.length;
  const optionCount = candidates.length + 1;
  const highlighted = Math.min(active, optionCount - 1);

  const choose = (index: number) => {
    if (index === createIndex) {
      if (!create.isPending) create.mutate(term);
      return;
    }
    const artist = candidates[index];
    if (artist) add({ id: artist.id, name: artist.name });
  };

  /** The combobox reflex (type → ArrowDown → Enter), which used to pick nothing. */
  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!listOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % optionCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + optionCount) % optionCount);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(highlighted);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="artist-search">{t('cms:songs.editor.artistsLabel')}</Label>
      <div
        className="flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-line bg-panel px-2 py-1.5"
        data-testid="artist-chips"
      >
        {value.map((artist, index) => (
          <span
            key={artist.id}
            draggable={!disabled}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (dragIndex !== null) onChange(move(value, dragIndex, index));
              setDragIndex(null);
            }}
            onDragEnd={() => setDragIndex(null)}
            data-testid={`artist-chip-${artist.id}`}
            className={cn(
              'flex items-center gap-1.5 rounded-full border border-line bg-canvas py-1 pr-1 pl-2 text-[12px]',
              dragIndex === index && 'opacity-50',
            )}
          >
            <span aria-hidden="true" className="cursor-grab text-muted">
              ⠿
            </span>
            <span className="font-semibold text-muted">{index + 1}.</span>
            <span className="font-semibold text-ink">{artist.name}</span>
            <button
              type="button"
              disabled={disabled || index === 0}
              onClick={() => onChange(move(value, index, index - 1))}
              aria-label={t('cms:songs.editor.moveUp')}
              className="px-1 text-muted hover:text-ink disabled:opacity-30"
            >
              ←
            </button>
            <button
              type="button"
              disabled={disabled || index === value.length - 1}
              onClick={() => onChange(move(value, index, index + 1))}
              aria-label={t('cms:songs.editor.moveDown')}
              className="px-1 text-muted hover:text-ink disabled:opacity-30"
            >
              →
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(value.filter((a) => a.id !== artist.id))}
              aria-label={t('cms:songs.editor.removeArtist', { name: artist.name })}
              className="rounded-full px-1.5 text-muted hover:bg-line hover:text-ink"
            >
              ✕
            </button>
          </span>
        ))}

        <Input
          id="artist-search"
          ref={input}
          className="h-7 w-44 flex-1 border-0 px-1 focus:border-0"
          placeholder={t('cms:songs.editor.addArtist')}
          aria-label={t('cms:songs.editor.addArtist')}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={listOpen ? optionId(highlighted) : undefined}
          value={search}
          disabled={disabled}
          onChange={(event) => {
            setSearch(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
        />
      </div>

      {listOpen && (
        <div
          id={listId}
          role="listbox"
          aria-label={t('cms:songs.editor.artistResults')}
          className="rounded-lg border border-line bg-panel py-1"
          data-testid="artist-results"
        >
          {results.isLoading && (
            <p className="px-3 py-2 text-[12px] text-muted">{t('common:states.loading')}</p>
          )}
          {!results.isLoading && candidates.length === 0 && (
            <p className="px-3 py-2 text-[12px] text-muted">
              {t('cms:songs.editor.noArtistResults')}
            </p>
          )}
          {candidates.map((artist, index) => (
            <button
              key={artist.id}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={index === highlighted}
              tabIndex={-1}
              className={cn(
                'flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] hover:bg-canvas',
                index === highlighted && 'bg-canvas',
              )}
              onClick={() => add({ id: artist.id, name: artist.name })}
            >
              <span>{artist.name}</span>
              <span className="text-[12px] text-muted">{artist.songCount}</span>
            </button>
          ))}
          <button
            id={optionId(createIndex)}
            type="button"
            role="option"
            aria-selected={createIndex === highlighted}
            tabIndex={-1}
            className={cn(
              'w-full px-3 py-1.5 text-left text-[13px] font-semibold text-pink hover:bg-canvas',
              createIndex === highlighted && 'bg-canvas',
            )}
            disabled={create.isPending}
            onClick={() => choose(createIndex)}
          >
            + {t('cms:songs.editor.createArtistTitle')}: “{term}”
          </button>
        </div>
      )}

      {/* UX-574: a permanent hint, not an assertive `role="alert"` that fires on every render. */}
      {value.length === 0 && <p className="text-[12px] text-danger">{t('cms:songs.noArtist')}</p>}
      {listOpen && (
        <Button
          variant="link"
          size="sm"
          className="self-start"
          onClick={() => {
            setOpen(false);
            input.current?.focus();
          }}
        >
          {t('common:actions.close')}
        </Button>
      )}
    </div>
  );
}
